import { useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router'

import Snackbar from '@mui/material/Snackbar'
import Typography from '@mui/material/Typography'

import usePubNub, { Channels } from '@shared/hooks/src/usePubNub'
import { useMe } from '@shared/providers/src/MeProvider'
import { convertPossibleRichTextToPlainText } from '@shared/utils'

import Avatar from '@components/Avatar'

import { NotificationCard, styles } from '../Notifications.utils'
import { useGetMessage } from './Messages.hooks'

/**
 * Messages component that listens for new message notifications via PubNub
 * and displays a Snackbar notification allowing the user to join the conversation.
 *
 * Led by the sender's own face rather than an icon: who a message is from is the first
 * thing worth knowing about it, and it is what tells this apart from the two
 * notifications either side of it without any of them having to shout.
 */
export default function Messages() {
  const testId = 'message-notification'

  const me = useMe()
  const currentPath = useLocation().pathname

  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState(undefined)

  const getMessage = useGetMessage()

  const handleClose = () => {
    setOpen(false)
    setTimeout(() => setMessage(undefined), 200)
  }

  usePubNub(`user_${me.id}`, async ({ action, attributes }) => {
    if (action !== Channels.NewMessage) return

    const path = window.location.pathname
    const { conversation_id: conversationId, message_id: messageId } = attributes

    // Don't show notification if the user is already in the conversation
    if (path.startsWith(`/app/messages/${conversationId}`)) return

    // Don't show notification if the user is on any visit page
    if (path.startsWith('/app/visits')) return

    const fetchedMessage = await getMessage.mutateAsync({ conversationId, messageId })
    setMessage({ conversationId, ...fetchedMessage })
    setOpen(true)
  })

  return (
    <Snackbar
      key={message?.conversationId}
      open={open}
      onClose={(event, reason) => {
        if (reason === 'escapeKeyDown') handleClose()
      }}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      data-testid={`${testId}-snackbar`}
    >
      <NotificationCard
        testId={testId}
        onClose={handleClose}
        leading={message?.user && <Avatar user={message.user} size="md" variant="circular" />}
        title={
          <Typography data-testid={`${testId}-author`} id={`${testId}-title`} sx={styles.title} noWrap>
            {message?.user.fullName}
          </Typography>
        }
        actionProps={{
          component: RouterLink,
          to: `/app/messages/${message?.conversationId}`,
          replace: currentPath.startsWith('/app/messages'),
          onClick: handleClose,
        }}
      >
        <Typography variant="body2" sx={styles.message} data-testid={`${testId}-content`} component="p" id={`${testId}-description`}>
          {convertPossibleRichTextToPlainText(message?.message)}
        </Typography>
      </NotificationCard>
    </Snackbar>
  )
}
